import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" });
}
function fmtMinutes(mins) {
  if (mins == null) return "—";
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
}

export default function EmployeeAttendanceAdminView({ employeeId }) {
  const [records, setRecords] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ check_in_time: "", check_out_time: "" });

  const load = () => api.get("/attendance/history", { params: { employee_id: employeeId } }).then((r) => setRecords(r.data)).catch(() => {});

  useEffect(() => { if (employeeId) load(); }, [employeeId]);

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      check_in_time: r.check_in_time ? r.check_in_time.slice(0, 16) : "",
      check_out_time: r.check_out_time ? r.check_out_time.slice(0, 16) : "",
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/attendance/${editingId}`, {
        check_in_time: editForm.check_in_time ? new Date(editForm.check_in_time).toISOString() : null,
        check_out_time: editForm.check_out_time ? new Date(editForm.check_out_time).toISOString() : null,
      });
      toast.success("Attendance record updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-2" data-testid="employee-attendance-admin-view">
      {records.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No attendance records yet.</p>}
      {records.map((r) => (
        <div key={r.id} data-testid={`admin-attendance-row-${r.id}`} className="p-3 border border-[#E2E8F0] rounded-md">
          {editingId === r.id ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid={`admin-attendance-checkin-input-${r.id}`} type="datetime-local" value={editForm.check_in_time} onChange={(e) => setEditForm({ ...editForm, check_in_time: e.target.value })} />
                <Input data-testid={`admin-attendance-checkout-input-${r.id}`} type="datetime-local" value={editForm.check_out_time} onChange={(e) => setEditForm({ ...editForm, check_out_time: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={saveEdit} data-testid={`admin-attendance-save-${r.id}`} className="bg-[#00823B] hover:bg-[#006A2F] text-white">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditingId(null)} data-testid={`admin-attendance-cancel-${r.id}`}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#1C1C1E]">{r.date}</p>
                <p className="text-xs text-[#4B5563] truncate">
                  {fmtTime(r.check_in_time)} → {fmtTime(r.check_out_time)} · Total {fmtMinutes(r.total_minutes)} · Break {fmtMinutes(r.break_minutes)} · Net {fmtMinutes(r.net_minutes)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge value={r.status} />
                <button data-testid={`admin-attendance-edit-${r.id}`} onClick={() => startEdit(r)} className="text-[#4B5563] hover:text-[#1C1C1E]"><Pencil className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
